import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';

import {
	defaultTQC1050_R01Dto,
	TQC1050_R01Dto,
} from './Dto_TQC1050_R01';

export interface TQC1050_R01Filter {
	compCd: string;
	transportDt: Date | undefined;	// 입고일자 (단일)
	customCd: string;
	customNm: string;
	brandCd: string;
	inoutClass: string;
}

export const defaultTQC1050_R01Filter: TQC1050_R01Filter = {
	compCd: '',
	transportDt: new Date(),
	customCd: '',
	customNm: '',
	brandCd: '',
	inoutClass: '',
};

/** 상단 InfoBar(검사자·승인자) 값. 저장 시 수정된 행에 적용한다. */
export interface TQC1050_R01Info {
	inspectorEmp: string;
	inspectorEmpNm: string;
	approvorEmpno: string;
	approvorEmpNm: string;
}

export const defaultTQC1050_R01Info: TQC1050_R01Info = {
	inspectorEmp: '',
	inspectorEmpNm: '',
	approvorEmpno: '',
	approvorEmpNm: '',
};

export interface Store_TQC1050_R01 {
	// 검색
	TQC1050_R01Filter: TQC1050_R01Filter;
	TQC1050_R01PastFilter: TQC1050_R01Filter;
	setTQC1050_R01Filter: (filter: Partial<TQC1050_R01Filter>) => void;
	setTQC1050_R01PastFilter: (filter: Partial<TQC1050_R01Filter>) => void;

	// InfoBar
	TQC1050_R01Info: TQC1050_R01Info;
	setTQC1050_R01Info: (info: Partial<TQC1050_R01Info>) => void;

	// 목록
	TQC1050_R01List: TQC1050_R01Dto[];
	setTQC1050_R01List: (list: TQC1050_R01Dto[]) => void;
	updateTQC1050_R01List: (valueIndex: number, data: TQC1050_R01Dto) => void;

	// 원본 (변경 감지)
	originalTQC1050_R01List: TQC1050_R01Dto[];
	setOriginalTQC1050_R01List: (list: TQC1050_R01Dto[]) => void;

	// 선택 (행 하이라이트)
	selectedTQC1050_R01List: string[];
	setSelectedTQC1050_R01List: (idList: string[]) => void;
	addSelectedTQC1050_R01List: (idList: string[]) => void;
	removeSelectedTQC1050_R01List: (idList?: string[]) => void;
	initSelectedTQC1050_R01List: () => void;
	allSelectedTQC1050_R01List: () => void;

	// 컬럼 상태
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

/**
 * 서버 목록에 그리드 식별/정렬용 인덱스를 1부터 부여한다.
 * 기본값 DTO 를 먼저 펼쳐 누락 필드를 채운 뒤 서버 값으로 덮어쓴다. (TQC1040_R01 패턴)
 */
const withIndex = (list: TQC1050_R01Dto[]): TQC1050_R01Dto[] => {
	let idx = 1;
	return list.map((item) => {
		const row = {
			...defaultTQC1050_R01Dto,
			...item,
			valueIndex: item.valueIndex > 0 ? item.valueIndex : idx,
			orderIndex: item.orderIndex > -1 ? item.orderIndex : idx,
			orderIndexSeq: Math.max(item.orderIndexSeq ?? 0, 0),
		};
		idx++;
		return row;
	});
};

export const useTQC1050_R01Store = create<Store_TQC1050_R01>((set) => ({
	TQC1050_R01Filter: defaultTQC1050_R01Filter,
	TQC1050_R01PastFilter: defaultTQC1050_R01Filter,
	setTQC1050_R01Filter: (filter) =>
		set((state) => ({ TQC1050_R01Filter: { ...state.TQC1050_R01Filter, ...filter } })),
	setTQC1050_R01PastFilter: (filter) =>
		set((state) => ({ TQC1050_R01PastFilter: { ...state.TQC1050_R01PastFilter, ...filter } })),

	TQC1050_R01Info: defaultTQC1050_R01Info,
	setTQC1050_R01Info: (info) =>
		set((state) => ({ TQC1050_R01Info: { ...state.TQC1050_R01Info, ...info } })),

	TQC1050_R01List: [],
	setTQC1050_R01List: (list) => set({ TQC1050_R01List: withIndex(list) }),
	updateTQC1050_R01List: (valueIndex, data) =>
		set((state) => ({
			TQC1050_R01List: state.TQC1050_R01List.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),

	originalTQC1050_R01List: [],
	setOriginalTQC1050_R01List: (list) => set({ originalTQC1050_R01List: withIndex(list) }),

	selectedTQC1050_R01List: [],
	setSelectedTQC1050_R01List: (idList) => set({ selectedTQC1050_R01List: idList }),
	addSelectedTQC1050_R01List: (idList) =>
		set((state) => ({
			selectedTQC1050_R01List: [...state.selectedTQC1050_R01List, ...idList],
		})),
	removeSelectedTQC1050_R01List: (idList?) =>
		set((state) => ({
			selectedTQC1050_R01List: state.selectedTQC1050_R01List.filter((v) =>
				idList ? !idList.includes(v) : false,
			),
		})),
	initSelectedTQC1050_R01List: () => set({ selectedTQC1050_R01List: [] }),
	allSelectedTQC1050_R01List: () =>
		set((state) => ({
			selectedTQC1050_R01List: state.TQC1050_R01List.map((item) => item.valueIndex.toString()),
		})),

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	initData: () =>
		set({
			TQC1050_R01Filter: defaultTQC1050_R01Filter,
			TQC1050_R01PastFilter: defaultTQC1050_R01Filter,
			TQC1050_R01Info: defaultTQC1050_R01Info,
			TQC1050_R01List: [],
			originalTQC1050_R01List: [],
			selectedTQC1050_R01List: [],
			columnState: null,
			scrollTop: null,
			allCount: 0,
		}),
}));

export default useTQC1050_R01Store;
